import styled from '@emotion/styled'
import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  Chip,
  Grid2,
  Stack,
  Table,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import moment from 'moment'
import React from 'react'
import { Link as ReactRouterLink, Switch, useParams, useRouteMatch } from 'react-router-dom'
import CommonPageWrapper from '../../../common/components/CommonPageWrapper'
import Loader from '../../../common/loader/Loader'
import { Query } from '../../../react-apollo/Components'
import RestrictedRoute from '../../auth/RestrictedRoute'
import { i18n } from '../../index/index'
import { fetchExpressCheckoutSchedulesByStationId } from '../expressCheckoutQueries'
import { RestaurantCategorySchedule } from '../types'
import { sortScheduleCategories } from '../util'
import { EditSchedulePage } from './EditSchedulePage'
import { NewScheduleFormPage } from './NewSchedulePage'

const PaddedCard = styled(Card)`
  padding: 10px;
`

function isAlreadyPassed(schedule: RestaurantCategorySchedule) {
  const today = moment().startOf('day').toDate()
  const endDate = moment(schedule.endDate, 'YYYY-MM-DD').endOf('day').toDate()
  return endDate < today
}

export function ExceptionalSchedulesRoot() {
  const match = useRouteMatch()
  const path = match.path
  return (
    <Switch>
      <RestrictedRoute exact requiredPermission='nav_basic' path={path} component={ExceptionalSchedulesPage} />
      <RestrictedRoute exact requiredPermission='nav_basic' path={`${path}/uusi`} component={NewScheduleFormPage} />
      <RestrictedRoute
        requiredPermission='nav_basic'
        exact
        path={`${path}/:scheduleId/muokkaa`}
        component={EditSchedulePage}
      />
    </Switch>
  )
}

export function ExceptionalSchedulesPage() {
  const { stationId } = useParams<{ stationId: string }>()
  const match = useRouteMatch()
  const url = match.url
  const [showPastSchedules, setShowPastSchedules] = React.useState(false)
  return (
    <CommonPageWrapper title={i18n.t('restaurantMenu.exceptionalSchedules')}>
      <Stack>
        <Grid2 container direction='row' justifyContent='left'>
          <Grid2>
            <Button component={ReactRouterLink} variant='outlined' to={`/pikakassa/${stationId}/aukioloajat`}>
              {i18n.t('buttons.back')}
            </Button>
          </Grid2>
        </Grid2>
        <Grid2 container direction='row' justifyContent='center'>
          <Grid2>
            <Typography variant='h4' sx={{ marginTop: '80px' }}>
              {i18n.t('restaurantMenu.exceptionalSchedules')}
            </Typography>
          </Grid2>
        </Grid2>
      </Stack>
      <Query<{ expressCheckoutSchedules?: RestaurantCategorySchedule[] }, { stationId: string }>
        query={fetchExpressCheckoutSchedulesByStationId}
        variables={{ stationId: decodeURIComponent(stationId) }}
        fetchPolicy='network-only'
      >
        {({ loading, data }) => {
          const schedules = (data?.expressCheckoutSchedules || [])
            .filter((schedule: RestaurantCategorySchedule) => {
              if (showPastSchedules) {
                return true
              }
              return !isAlreadyPassed(schedule)
            })
            .slice(0)
            .sort((a: RestaurantCategorySchedule, b: RestaurantCategorySchedule) => {
              a.endDate
            })
          return (
            <Loader loading={loading}>
              <Stack sx={{ marginTop: '20px' }} spacing={2}>
                {schedules.map((schedule) => (
                  <ExceptionalSchedule key={schedule.id} exceptionalSchedule={schedule} url={url} />
                ))}
                <Stack spacing={2}>
                  <PaddedCard>
                    <Stack spacing={2} alignItems='center'>
                      <Button
                        component={ReactRouterLink}
                        to={`${url}/uusi`}
                        variant='contained'
                        startIcon={<AddIcon />}
                      >
                        {i18n.t('restaurantMenu.addNewExceptionalSchedule')}
                      </Button>
                    </Stack>
                  </PaddedCard>
                </Stack>
                <Stack spacing={2} alignItems='center'>
                  <Button onClick={() => setShowPastSchedules(!showPastSchedules)}>
                    {showPastSchedules
                      ? i18n.t('restaurantMenu.hidePastSchedules')
                      : i18n.t('restaurantMenu.showPastSchedules')}
                  </Button>
                </Stack>
              </Stack>
            </Loader>
          )
        }}
      </Query>
    </CommonPageWrapper>
  )
}

interface ExceptionalScheduleProps {
  exceptionalSchedule: RestaurantCategorySchedule
  url: string
}

function ExceptionalSchedule({ exceptionalSchedule, url }: ExceptionalScheduleProps) {
  const isActive = moment().isBetween(
    moment(exceptionalSchedule.startDate, 'YYYY-MM-DD').startOf('day'),
    moment(exceptionalSchedule.endDate, 'YYYY-MM-DD').endOf('day'),
  )
  return (
    <Grid2>
      <PaddedCard>
        <Stack spacing={2}>
          <Stack direction='row' spacing={2} alignItems='center'>
            <Typography variant='h5'>{exceptionalSchedule.name}</Typography>
            {isActive && <Chip size='small' color='primary' label={i18n.t('restaurantMenu.currentlyActive')} />}
          </Stack>
          <Typography variant='subtitle1'>
            {moment(exceptionalSchedule.startDate, 'YYYY-MM-DD').format('DD.MM.YYYY')} -{' '}
            {moment(exceptionalSchedule.endDate, 'YYYY-MM-DD').format('DD.MM.YYYY')}
          </Typography>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='subtitle2'>{i18n.t('restaurantMenu.categories')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <Table>
                {sortScheduleCategories(exceptionalSchedule.categories).map((category) => {
                  const { opens, closes, schedules } = category
                  return (
                    <TableRow key={`${exceptionalSchedule.id}-${category.id}`}>
                      <TableCell>
                        <Typography>{category.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <OpeningTimesCell opens={opens} closes={closes} schedules={schedules} />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </Table>
            </AccordionDetails>
          </Accordion>
          <Stack direction='row' justifyContent='flex-end' spacing={2}>
            <Button
              component={ReactRouterLink}
              variant='outlined'
              sx={{ marginRight: '10px' }}
              to={{ pathname: `${url}/uusi`, search: `?fromScheduleId=${exceptionalSchedule.id}` }}
            >
              {i18n.t('restaurantMenu.useForNewSchedule')}
            </Button>
            <Button component={ReactRouterLink} variant='contained' to={`${url}/${exceptionalSchedule.id}/muokkaa`}>
              {i18n.t('buttons.edit')}
            </Button>
          </Stack>
        </Stack>
      </PaddedCard>
    </Grid2>
  )
}

function OpeningTimesCell({
  opens,
  closes,
  schedules,
}: {
  opens?: string | null
  closes?: string | null
  schedules?: { opens: string; closes: string }[] | null
}) {
  if (opens === '00:00' && closes === '00:00') {
    return <Chip size='small' color='warning' label={i18n.t('restaurantMenu.closed')} />
  }
  if (opens === '00:00' && closes === '23:59') {
    return <Chip size='small' color='primary' label={i18n.t('restaurantMenu.alwaysOpen')} />
  }
  if (schedules && schedules.length > 0) {
    return (
      <>
        {schedules.map((schedule, index) => (
          <div key={index}>
            {schedule.opens} - {schedule.closes}
          </div>
        ))}
      </>
    )
  }
  return (
    <>
      {opens} - {closes}
    </>
  )
}
